/** 无数据时 body 区域固定高度（px），仅组件内部使用 */
export const DEFAULT_EMPTY_BODY_HEIGHT = 200
/** 有数据时 body 最多展示行数，仅组件内部使用 */
export const DEFAULT_MAX_VISIBLE_ROWS = 10
export const GRID_SCROLL_X_HEIGHT = 14

export interface ResolveViewportHeightOptions {
  rowHeight: number
  rowCount: number
  showFooter?: boolean
  useFixedXScroll?: boolean
  height?: string | number | null
}

export function parseGridHeight(value?: string | number | null): number | null {
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'number') {
    return value
  }
  const num = parseFloat(value)
  return Number.isFinite(num) ? num : null
}

/** 固定 height 下 body 区域可用高度（扣除表头/表尾/横向滚动条） */
export function resolveFixedBodyHeight(options: {
  height: string | number
  rowHeight: number
  showFooter?: boolean
  useFixedXScroll?: boolean
}): number {
  const height = parseGridHeight(options.height) ?? 0
  let used = options.rowHeight
  if (options.showFooter) {
    used += options.rowHeight
  }
  if (options.useFixedXScroll) {
    used += GRID_SCROLL_X_HEIGHT
  }
  return Math.max(options.rowHeight, height - used)
}

/** 是否需要纵向滚动：固定高度 / fill 铺满时按内容是否溢出 body，autoHeight 按 10 行封顶 */
export function needsGridVerticalScroll(options: {
  rowCount: number
  rowHeight: number
  height?: string | number | null
  showFooter?: boolean
  useFixedXScroll?: boolean
  /** autoHeight=false 且未传 height：跟随父容器铺满 */
  fill?: boolean
  /** fill 模式下实测 body 可视高度（px） */
  measuredBodyHeight?: number | null
}): boolean {
  if (options.rowCount === 0) {
    return false
  }
  const contentHeight = options.rowCount * options.rowHeight
  const fixedHeight = parseGridHeight(options.height ?? null)
  if (fixedHeight != null) {
    const bodyHeight = resolveFixedBodyHeight({
      height: fixedHeight,
      rowHeight: options.rowHeight,
      showFooter: options.showFooter,
      useFixedXScroll: options.useFixedXScroll,
    })
    return contentHeight > bodyHeight
  }
  if (options.fill) {
    const measured = options.measuredBodyHeight
    if (measured == null || measured <= 0) {
      // 尚未量到容器高度时先按内容是否超过空态高度粗判，避免首屏闪断滚动
      return contentHeight > DEFAULT_EMPTY_BODY_HEIGHT
    }
    return contentHeight > measured
  }
  return options.rowCount > DEFAULT_MAX_VISIBLE_ROWS
}

export interface ResolveBodyAreaHeightOptions {
  rowHeight: number
  rowCount: number
  height?: string | number | null
  showFooter?: boolean
  useFixedXScroll?: boolean
  /** autoHeight=false：body 高度由 flex 铺满，不按行数封顶 */
  fill?: boolean
}

/** body 区域高度：无数据 200px；固定 height 用剩余 body 高度；fill 为 0；否则按行数且最多 10 行 */
export function resolveBodyAreaHeight(
  options: ResolveBodyAreaHeightOptions
): number {
  const {
    rowHeight,
    rowCount,
    height,
    showFooter,
    useFixedXScroll,
    fill,
  } = options

  if (fill) {
    return 0
  }

  if (rowCount === 0) {
    return DEFAULT_EMPTY_BODY_HEIGHT
  }

  const fixedHeight = parseGridHeight(height ?? null)
  if (fixedHeight != null) {
    return resolveFixedBodyHeight({
      height: fixedHeight,
      rowHeight,
      showFooter,
      useFixedXScroll,
    })
  }

  const contentHeight = Math.min(rowCount, DEFAULT_MAX_VISIBLE_ROWS) * rowHeight
  return Math.max(DEFAULT_EMPTY_BODY_HEIGHT, contentHeight)
}

/** 表头 + body + 表尾 + 横向滚动条 */
export function resolveViewportHeight(
  options: ResolveViewportHeightOptions
): number {
  const {
    rowHeight,
    rowCount,
    showFooter = false,
    useFixedXScroll = false,
  } = options

  const bodyHeight = resolveBodyAreaHeight({
    rowHeight,
    rowCount,
    height: options.height,
    showFooter,
    useFixedXScroll,
  })

  let height = rowHeight + bodyHeight
  if (showFooter) {
    height += rowHeight
  }
  if (useFixedXScroll) {
    height += GRID_SCROLL_X_HEIGHT
  }
  return height
}
